import React, { useState } from "react";
import { useForm } from "react-hook-form";
import { useTranslation } from "react-i18next";
import { yupResolver } from "@hookform/resolvers/yup";

import InputWrapper from "../utilities/formElements/commonInput/InputWrapper";
import CommonButton from "../utilities/formElements/commonButton/CommonButton";
import { changeEmailValidationSchema } from "../utilities/validationSchema";
import { toastMessageError } from "../utilities/commonToastMessage";

import { requestEmailChange } from "../../services/user";

import { CommonModalProps } from "../../Interfaces/CommonModalInterface";
import { typePassword, typeText } from "../../constants/commonConstants";

import EyeIcon from "../../assets/images/eye-outline.svg";
import EyeCloseIcon from "../../assets/images/hidden-password.svg";
import CheckMark from "../../assets/images/check-mark.svg";

interface IChangeEmail {
  newEmail: string;
  password: string;
}

// "Change your email" - requested directly 2026-08-07, industry-standard
// verify-before-switch flow (see UserService.requestEmailChange/
// confirmEmailChange on the backend for the full design). This is step 1
// only: submitting here sends a confirmation link to the NEW address -
// nothing on the account actually changes until that link is clicked
// (ConfirmEmailChange.tsx handles step 2). Mirrors ChangePassword.tsx's
// structure (same modal shape, same "stay open with a real confirmation"
// pattern instead of an immediate silent close).
const ChangeEmail: React.FC<CommonModalProps> = ({ title, onClose }) => {
  const { t: translation } = useTranslation();
  const [loading, setLoading] = useState(false);
  const [viewPassword, setViewPassword] = useState(false);
  const [successModal, setSuccessModal] = useState(false);

  const {
    handleSubmit,
    control,
    formState: { errors },
  } = useForm({
    resolver: yupResolver(changeEmailValidationSchema(translation)),
  });

  const onSubmit = async (data: IChangeEmail) => {
    setLoading(true);
    const result = await requestEmailChange({
      newEmail: data.newEmail,
      password: data.password,
    });
    setLoading(false);
    if (result && result.data) {
      setSuccessModal(true);
    } else {
      toastMessageError(
        result?.error?.message || "Something went wrong, please try again",
        "emailChangeRequestFailed"
      );
    }
  };

  if (successModal) {
    return (
      <div className="modal show-modal common-modal">
        <div className="modal-dialog modal-lg">
          <div className="modal-content">
            <div className="modal-body common-body text-primary tracking-code-modal">
              <img src={CheckMark} alt="success" className="successful-icon" />
              <div className="congrats-header">
                <h4>Check your new inbox</h4>
                <p className="font12">
                  We sent a confirmation link to your new email address.
                  Your login email won't change until you click it - and
                  it'll expire in 1 hour.
                </p>
              </div>
              <div className="button-align-modal flex-align-modal-btn">
                <CommonButton
                  className="theme-btn success-btn text-uppercase fontBold"
                  type="button"
                  label={translation("ok")}
                  onClick={onClose}
                />
              </div>
            </div>
          </div>
        </div>
      </div>
    );
  }

  return (
    <div className="modal show-modal common-modal">
      <div className="modal-dialog modal-lg">
        <div className="modal-content">
          <div className="modal-header">
            <h4 className="modal-title">{title}</h4>
            <CommonButton
              type="button"
              className="btnC btn-close btn-close-white"
              onClick={onClose}
              data-bs-dismiss="modal"
            />
          </div>

          <div className="modal-body common-body text-primary">
            <form onSubmit={handleSubmit((data) => onSubmit(data as IChangeEmail))}>
              <div className="form-group">
                <label>New email address</label>
                <InputWrapper
                  control={control}
                  name="newEmail"
                  className="form-control"
                  placeholder="Enter your new email address"
                  error={errors?.newEmail}
                  type="email"
                />
              </div>

              <div className="form-group">
                {/* Not "Old Password": nothing here replaces a password. This field
                    confirms the customer is who they say they are before an email
                    change is sent. "Old" implies a new one is about to be set. */}
                <label>{translation("current_password_confirm")}</label>
                <div className="input-icon">
                  <InputWrapper
                    control={control}
                    name="password"
                    className="form-control"
                    placeholder={translation("current_password_confirm")}
                    error={errors?.password}
                    type={viewPassword ? typeText : typePassword}
                  />
                  <CommonButton
                    type="button"
                    onClick={() => setViewPassword(!viewPassword)}
                    iconSrc={viewPassword ? EyeIcon : EyeCloseIcon}
                  />
                </div>
              </div>

              <div className="text-center mt-4 mb-3">
                <CommonButton
                  type="submit"
                  className="theme-btn success-btn text-uppercase fontBold"
                  label="Send Confirmation Link"
                  loading={loading}
                  disabled={loading}
                />
              </div>
            </form>
          </div>
        </div>
      </div>
    </div>
  );
};

export default ChangeEmail;
